import type { Copy } from './en';
import { LOCALE_INFO } from './locale';
import { joinThaiCompounds } from './wordJoiner';

/**
 * Thai copy. Terms follow the app's own app_th.arb (ทริป, เช็กอิน, แผนการเดินทาง, แผน, ค้นพบ,
 * ที่อยู่หลัก, พาสปอร์ต, แสตมป์, เพิ่มลงในทริปของฉัน) so the site and app read the same.
 * Machine-translated; due a review by a Thai reader. Headings and buttons go through joinThaiCompounds so they
 * wrap between words, not inside them.
 */

export const th: Copy = {
  htmlLang: LOCALE_INFO.th.htmlLang,
  phraseGap: ' ',
  ogLocale: 'th_TH',
  languageName: 'ไทย',

  nav: {
    home: 'หน้าหลัก Travo',
    primary: 'เมนูหลัก',
    features: 'ฟีเจอร์',
    plan: 'วางแผนทริป',
    about: 'เกี่ยวกับเรา',
    download: 'ดาวน์โหลดแอป',
    openMenu: 'เปิดเมนู',
    closeMenu: 'ปิดเมนู',
    language: 'ภาษา',
    byLabs: 'โดย Travo Labs'
  },

  download: {
    appStore: 'ดาวน์โหลดบน App Store',
    playStore: 'ดาวน์โหลดบน Google Play',
    ios: 'iOS (App Store)',
    android: 'Android (Google Play)',
    free: 'ฟรี · iOS & ANDROID'
  },

  seo: {
    ogImage: '/og-image-th.png',
    ogImageAlt: 'Travo — ชีวิตเดินไปเร็ว Travo จำไว้ให้ แอปบันทึกการเดินทาง พาสปอร์ต และวางแผนทริปสำหรับ iOS และ Android',
    home: {
      title: 'Travo: แอปบันทึกการเดินทางอัตโนมัติ พาสปอร์ต และวางแผนเที่ยว',
      description:
        'Travo แอปเที่ยวที่เริ่มทริปให้เอง บันทึกทุกเมืองและทุกประเทศ ประทับแสตมป์ลงพาสปอร์ตทุกครั้งที่ไปประเทศใหม่ และช่วยวางแผนเที่ยวทริปถัดไปแบบวันต่อวัน ใช้ฟรีบน iOS และ Android',
      keywords:
        'แอปบันทึกการเดินทาง, แอปเที่ยว, แอปวางแผนเที่ยว, บันทึกทริปอัตโนมัติ, พาสปอร์ตการเดินทาง, ประเทศที่เคยไป, วางแผนทริป, แผนการเดินทาง, ไดอารี่การเดินทาง, เช็กอิน, แชร์แผนเที่ยว, แผนที่การเดินทาง, Travo'
    },
    about: {
      title: 'เกี่ยวกับ Travo: เก็บเรื่องราวของคุณไว้ทุกวัน',
      description:
        'เราสร้าง Travo เพราะชีวิตเดินไปเร็ว และช่วงเวลาที่ดีที่สุดของคุณไม่ควรถูกลืม Travo บันทึกทริปให้โดยไม่ต้องทำอะไร เก็บช่วงเวลาที่คุณเลือก และช่วยวางแผนทริปถัดไป',
      keywords:
        'เกี่ยวกับ Travo, พันธกิจ Travo Labs, บันทึกการเดินทางอัตโนมัติ, พาสปอร์ตการเดินทาง, วางแผนทริป, ไดอารี่การเดินทาง, แอปเดินทางที่ใส่ใจความเป็นส่วนตัว'
    }
  },

  hero: {
    eyebrow: 'ทริปเริ่มเอง',
    titleLead: joinThaiCompounds('ชีวิตเดินไปเร็ว'),
    titleAccent: joinThaiCompounds('Travo จำไว้ให้'),
    body: 'แค่ออกจากบ้าน Travo ก็เริ่มทริปให้คุณ — บันทึกทุกเมืองและทุกประเทศ ประทับแสตมป์ลงพาสปอร์ต และเก็บแผนสำหรับทริปถัดไป คุณแค่ออกเดินทาง',
    photoAlt: 'บอลลูนลอยเหนือหุบเขายามพระอาทิตย์ขึ้น',
    phoneAlt: 'แผนที่หน้าหลักของ Travo แสดงไทเปพร้อมหมุดเช็กอินรูปภาพ และโหมดแผนที่การเดินทางกับ F1',
    stampLabel: 'ไปรษณีย์อากาศ'
  },

  band: ['แผนที่', 'ทริป', 'เช็กอิน', 'สถานที่', 'เพื่อน', 'พาสปอร์ต', 'แผนการเดินทาง', 'ค้นพบ'],

  features: {
    title: joinThaiCompounds('แปดจุดแวะพัก\nเพื่อนร่วมทางหนึ่งเดียว'),
    body: 'จำที่ที่คุณเคยไป เก็บช่วงเวลาสำคัญ และวางแผนครั้งต่อไป การเดินทางกับ Travo เริ่มต้นจากแปดจุดแวะพักนี้',
    items: [
      {
        tag: '01 · แผนที่',
        title: joinThaiCompounds('การเดินทางทั้งหมดในที่เดียว'),
        body: 'ทริป สถานที่ที่บันทึกไว้ เช็กอิน และเพื่อน — รวมอยู่บนแผนที่เดียว แตะเพื่อสลับเลเยอร์ แล้วดูโลกของคุณเติมเต็มขึ้นทุกครั้งที่ออกสำรวจ'
      },
      {
        tag: '02 · ทริป',
        title: joinThaiCompounds('แค่ออกเดินทาง Travo จำไว้ให้'),
        body: 'ออกจากบ้านแล้วทริปจะเริ่มเองโดยอัตโนมัติ Travo บันทึกย่าน เมือง และประเทศที่คุณไป ไม่ต้องพิมพ์อะไรเลย'
      },
      {
        tag: '03 · เช็กอิน',
        title: joinThaiCompounds('เก็บช่วงเวลานั้นไว้'),
        body: 'เช็กอินได้สูงสุดวันละสามครั้ง เพิ่มรูปภาพเพื่อจดจำทั้งช่วงเวลาและสถานที่'
      },
      {
        tag: '04 · สถานที่',
        title: joinThaiCompounds('เก็บไว้ไปทีหลัง'),
        body: 'รวบรวมสถานที่เป็นรายการสำหรับทริปถัดไป หรือนำรายการที่บันทึกใน Google Maps เข้ามาใน Travo ได้ด้วยการแชร์ครั้งเดียว'
      },
      {
        tag: '05 · เพื่อน',
        title: joinThaiCompounds('แบ่งปันการเดินทาง'),
        body: 'ดูเช็กอินล่าสุดของเพื่อน และแลกการ์ดแสตมป์กันด้วยการสแกน QR'
      },
      {
        tag: '06 · พาสปอร์ต',
        title: joinThaiCompounds('พาสปอร์ตแห่งความทรงจำ'),
        body: 'สะสมแสตมป์จากทุกประเทศที่คุณไป บันทึกเที่ยวบินแล้วดูว่าคุณบินมาไกลแค่ไหน'
      },
      {
        tag: '07 · แผนการเดินทาง',
        title: joinThaiCompounds('วางแผนไปด้วยกัน'),
        body: 'สร้างแผนการเดินทางรายวันจากสถานที่ที่บันทึกไว้ ที่พัก และเที่ยวบิน ชวนเพื่อนมาวางแผนด้วยกัน'
      },
      {
        tag: '08 · ค้นพบ',
        title: joinThaiCompounds('ค้นหาการผจญภัยครั้งต่อไป'),
        body: 'สำรวจแผนการเดินทางที่นักเดินทางคนอื่นแชร์ไว้ เพิ่มลงในทริปของคุณแล้วปรับให้เป็นแบบของคุณเอง'
      }
    ],
    extrasLabel: 'ยังมีอีก',
    extras: [
      'วิดเจ็ตหน้าจอหลัก: พาสปอร์ต ทริปที่กำลังจะมาถึง แผนการเดินทาง',
      'สรุปประจำเดือนไว้แชร์',
      'ปลั๊กอิน F1: การแข่งขัน ผลการแข่ง และตารางคะแนนบนแผนที่ของคุณ'
    ]
  },

  checkins: {
    eyebrow: '03 · เช็กอิน',
    title: joinThaiCompounds('ประทับตราวันของคุณ\nได้สูงสุดวันละสามครั้ง'),
    body: 'Travo บันทึกชีวิตประจำวันให้เอง ส่วนเช็กอินคือสิ่งที่คุณเลือกเอง — เพิ่มรูปภาพ แล้วแผนที่จะจำได้ว่าคุณยืนอยู่ตรงไหน วันละไม่กี่ครั้ง ก็กลายเป็นไดอารี่การเดินทางของคุณ',
    stamps: ['มื้ออร่อย', 'จุดชมวิวสวย ๆ', 'ไปเที่ยวสนุก ๆ'],
    screenshotAlt: 'ปฏิทินไฮไลต์ประจำวันของเดือนมิถุนายน 2026 พร้อมเช็กอินรูปภาพและทริปไอซ์แลนด์'
  },

  passport: {
    eyebrow: '06 · พาสปอร์ต',
    title: joinThaiCompounds('ทุกประเทศได้แสตมป์หนึ่งดวง'),
    body: 'พาสปอร์ตของคุณเติมเต็มเองระหว่างการเดินทาง — หน้าแสตมป์สำหรับทุกปี พร้อมนับจำนวนประเทศและวันที่อยู่นอกบ้านให้คุณ',
    points: [
      'หน้าแสตมป์ของแต่ละปี พร้อมนับจำนวนประเทศและวันที่อยู่นอกบ้าน',
      'บันทึกเที่ยวบินลงพาสปอร์ต พร้อมระยะทางที่บินเป็นกิโลเมตร',
      'สรุปรูปภาพและสถานที่ประจำเดือน พร้อมแชร์',
      'วิดเจ็ตพาสปอร์ตสำหรับหน้าจอหลัก'
    ],
    sample: 'ตัวอย่าง',
    thailand: 'ไทย',
    indonesia: 'อินโดนีเซีย',
    japanAlt: 'แสตมป์ตัวอย่าง: ญี่ปุ่น เกียวโต',
    taiwanAlt: 'แสตมป์ตัวอย่าง: ไต้หวัน ไทเป'
  },

  plan: {
    eyebrow: '07 & 08 · แผนการเดินทาง + ค้นพบ',
    title: joinThaiCompounds('วางแผนแต่ละวัน หรือเริ่มจากแผนของคนอื่น'),
    body: 'เปลี่ยนสถานที่ที่บันทึกไว้ให้เป็นแผนการเดินทางรายวัน — แผน ที่พัก และเที่ยวบินในที่เดียว แชร์ให้เพื่อนแก้ไขด้วยกันได้ หรือเข้าไปที่ค้นพบ เพิ่มทริปที่มีคนวางแผนไว้แล้ว และปรับให้เป็นของคุณ',
    points: [
      'แผน ที่พัก และเที่ยวบินบนไทม์ไลน์รายวันเดียวกัน',
      'รายการตรวจสอบการจองสำหรับทุกอย่างที่ยังต้องจอง',
      'วิดเจ็ตหน้าจอหลักที่แสดงสิ่งที่กำลังทำอยู่และถัดไป',
      'ค้นพบ: ค้นหาแผนตามประเทศและจำนวนวัน แล้วเพิ่มลงในทริปของคุณ'
    ],
    widget: {
      day: 'วันที่ 2/10 · GOLDEN CIRCLE',
      label: 'วิดเจ็ต',
      nowLabel: 'ตอนนี้ · 09:30',
      now: 'อุทยานแห่งชาติทิงเวทลีร์',
      nextLabel: 'ถัดไป · 12:30',
      next: 'เกย์ซีร์และสโตรกกูร์',
      later: 'ภายหลังวันนี้: กุตล์ฟอสส์',
      more: '+อีก 1 รายการ'
    },
    discover: {
      kicker: 'ค้นพบ · ไอซ์แลนด์',
      length: '10 วัน',
      title: joinThaiCompounds('โรดทริปไอซ์แลนด์ 10 วัน'),
      byline: 'โดย Travo (@travo) · เพิ่มแล้ว 999 ครั้ง',
      cta: joinThaiCompounds('เพิ่มลงในทริปของฉัน')
    }
  },

  how: {
    eyebrow: 'เส้นทาง',
    title: joinThaiCompounds('ตั้งค่าครั้งเดียว แล้วออกเดินทางได้เลย'),
    steps: [
      {
        tag: 'ที่อยู่หลัก',
        title: joinThaiCompounds('บอก Travo ว่าบ้านอยู่ที่ไหน'),
        body: 'Travo จะได้รู้ว่าเมื่อไหร่คุณกำลังเดินทาง และเมื่อไหร่กลับถึงบ้านแล้ว'
      },
      {
        tag: 'ออกเดินทาง',
        title: joinThaiCompounds('อนุญาตตำแหน่งครั้งเดียว'),
        body: 'ติดตามตำแหน่งเบื้องหลังแบบประหยัดแบตเตอรี่ ปิดได้ทุกเมื่อในการตั้งค่า'
      },
      {
        tag: 'ระหว่างทาง',
        title: joinThaiCompounds('ออกจากบ้าน'),
        body: 'ทริปจะเริ่มเอง และทุกเมืองทุกประเทศจะถูกบันทึกไว้ระหว่างทาง'
      },
      {
        tag: 'ถึงปลายทาง',
        title: joinThaiCompounds('ประทับ วางแผน แชร์'),
        body: 'เช็กอินช่วงเวลาที่ดีที่สุด เติมพาสปอร์ตให้เต็ม และวางแผนทริปถัดไปกับเพื่อน'
      }
    ],
    note: 'เคยเดินทางมาก่อนแล้วใช่ไหม Travo ตรวจหาทริปที่ผ่านมาจากคลังรูปภาพของคุณได้ โดยอ่านเฉพาะตำแหน่งที่ถ่ายรูปแต่ละรูป — รูปภาพของคุณจะไม่ถูกอัปโหลดหรือจัดเก็บ'
  },

  friends: {
    eyebrow: '05 · เพื่อน',
    title: joinThaiCompounds('เดินทางด้วยกัน แม้อยู่ห่างกัน'),
    body: 'เพิ่มเพื่อนแล้วดูว่าพวกเขาเช็กอินล่าสุดที่ไหน — บนแผนที่ของคุณ แลกการ์ดแสตมป์ด้วย QR code หรือส่งลิงก์โปรไฟล์ของคุณ สแกนเพื่อติดตามเส้นทาง',
    to: 'ถึง: ทุกคนที่เคยเดินทางด้วยกัน',
    from: 'จาก: app.travolabs.com/@travo',
    cta: joinThaiCompounds('ดูวิดีโอสาธิต'),
    caption: 'ส่งความคิดถึงจากทุกที่ที่คุณเคยไป',
    mapAlt: 'แผนที่โลกพร้อมพาสปอร์ต กล้อง และของที่ระลึกจากการเดินทาง'
  },

  cta: {
    eyebrow: 'ไปรษณีย์อากาศ · ชั้นหนึ่ง',
    title: joinThaiCompounds('พาสปอร์ตของคุณรออยู่'),
    body: 'ดาวน์โหลด Travo ฟรี ทั้งชีวิตประจำวัน ทริปสุดสัปดาห์ และทริปถัดไปที่คุณกำลังวางแผน — รวมอยู่ในที่เดียว'
  },

  footer: {
    tagline: 'จดจำทุกการเดินทาง วางแผนครั้งต่อไป',
    product: 'ผลิตภัณฑ์',
    community: 'ชุมชน',
    legal: 'ข้อกฎหมาย',
    privacy: 'นโยบายความเป็นส่วนตัว',
    terms: 'ข้อกำหนดการใช้งาน',
    rights: 'TRAVO LABS. สงวนลิขสิทธิ์'
  },

  about: {
    eyebrow: 'เกี่ยวกับ Travo Labs',
    titleLead: joinThaiCompounds('ชีวิตเดินไปเร็ว'),
    titleAccent: joinThaiCompounds('Travo จำไว้ให้'),
    intro: [
      'เราสร้าง Travo เพื่อช่วยให้คุณเก็บสถานที่และช่วงเวลาที่ทำให้ชีวิตเป็นของคุณ ตั้งแต่ถนนที่คุ้นเคยไปจนถึงเมืองที่ห่างไกล Travo บันทึกการเดินทางของคุณอย่างเงียบ ๆ — คุณจึงได้อยู่กับปัจจุบันเต็มที่',
      'ทริปเริ่มเองโดยอัตโนมัติ ทุกประเทศที่คุณไปจะเพิ่มแสตมป์ลงในพาสปอร์ตการเดินทาง สถานที่ที่คุณบันทึกไว้จะกลายเป็นจุดเริ่มต้นของการผจญภัยครั้งต่อไป',
      'เปิด Travo เพื่อย้อนดูที่ที่คุณเคยไป ติดตามการเดินทางของเพื่อน และวางแผนว่าจะไปที่ไหนต่อ'
    ],
    photoAlt: 'เส้นขอบฟ้าของเมืองยามค่ำคืน',
    caption: 'ส่งความคิดถึงจากระหว่างทาง',
    captionTag: 'NO. 00 · ABOUT',
    stampLabel: 'ไปรษณีย์อากาศ',
    missionEyebrow: 'พันธกิจของเรา',
    missionLead: joinThaiCompounds('ทำให้การบันทึกเส้นทางชีวิตของคุณ'),
    missionAccent: joinThaiCompounds('ง่ายดาย'),
    missionTail: joinThaiCompounds('และมีความหมาย'),
    salutation: 'ถึงนักเดินทาง',
    letter: [
      'เราเชื่อว่าแอปเดินทางที่ดีที่สุด คือแอปที่คุณลืมไปเลยว่ากำลังใช้อยู่ Travo รู้ว่าเมื่อไหร่คุณออกจากบ้าน และเริ่มทริปให้คุณ บันทึกทุกย่าน ทุกเมือง และทุกประเทศ — ตั้งแต่ถนนหน้าบ้านไปจนถึงอีกฟากหนึ่งของโลก',
      'ที่เหลือคุณเลือกเอง: เช็กอินไม่กี่ครั้งสำหรับช่วงเวลาที่มีความหมาย พาสปอร์ตที่เต็มไปด้วยแสตมป์ แผนที่วางร่วมกับเพื่อนหรือยืมจากนักเดินทางคนอื่น คุณแค่ใส่ใจกับทริป Travo จะเก็บบันทึกไว้ให้ อีกหลายปีข้างหน้า คุณจะมีทุกการผจญภัย ทั้งเล็กและใหญ่ อยู่ตรงที่มันเกิดขึ้นจริง'
    ],
    signature: '— Travo',
    whyTitle: joinThaiCompounds('ทำไมต้อง Travo'),
    whyBody: 'สี่คำสัญญา ประทับตราและปิดผนึกแล้ว',
    reasons: [
      {
        title: joinThaiCompounds('ทริปเริ่มเอง'),
        body: 'ออกจากที่อยู่หลักแล้วทริปจะเริ่มเอง ทุกเมืองและทุกประเทศถูกบันทึกไว้ และพาสปอร์ตของคุณจะสะสมแสตมป์'
      },
      {
        title: joinThaiCompounds('ช่วงเวลาที่คุณเลือก'),
        body: 'เช็กอินได้สูงสุดวันละสามครั้ง พร้อมรูปภาพที่ปักหมุดตรงจุดที่คุณยืน กลายเป็นไดอารี่การเดินทางที่อยากกลับมาอ่านซ้ำ'
      },
      {
        title: joinThaiCompounds('วางแผนไปด้วยกัน'),
        body: 'บันทึกสถานที่ สร้างแผนการเดินทางรายวันที่เพื่อนแก้ไขได้ หรือเริ่มจากแผนที่นักเดินทางคนอื่นเผยแพร่ไว้'
      },
      {
        title: joinThaiCompounds('ออกแบบมาเพื่อความเป็นส่วนตัว'),
        body: 'คุณปิดการติดตามได้ทุกเมื่อ การตรวจหาทริปที่ผ่านมาอ่านเฉพาะตำแหน่งที่ถ่ายรูป — รูปภาพไม่เคยออกจากโทรศัพท์ของคุณ'
      }
    ],
    ctaEyebrow: 'ตราประทับถัดไปของคุณ',
    ctaTitle: joinThaiCompounds('ใช้ชีวิตให้เต็มที่ เราจะจดไว้ให้'),
    ctaBody: 'Travo ใช้ฟรีบน iOS และ Android อนุญาตครั้งเดียว แล้วแผนที่ของคุณจะเริ่มเติมเต็มเอง',
    breadcrumbAbout: 'เกี่ยวกับเรา'
  },

  schema: {
    appDescription:
      'แอปบันทึกการเดินทางแบบไม่ต้องทำอะไร พาสปอร์ต และวางแผนทริป ทริปเริ่มเองเมื่อคุณออกจากที่อยู่หลัก ทุกเมืองและทุกประเทศถูกบันทึกไว้ และทุกประเทศใหม่จะเพิ่มแสตมป์ลงพาสปอร์ต เช็กอินช่วงเวลาได้สูงสุดวันละสามครั้ง วางแผนการเดินทางรายวันกับเพื่อน หรือเริ่มจากแผนที่เผยแพร่ในหน้าค้นพบ',
    featureList: [
      'ทริปอัตโนมัติที่เริ่มเมื่อคุณออกจากที่อยู่หลัก',
      'เช็กอิน — ช่วงเวลาพร้อมรูปภาพได้สูงสุดวันละสามครั้ง',
      'พาสปอร์ตการเดินทางพร้อมแสตมป์ของทุกประเทศและเที่ยวบินที่บิน',
      'แผนการเดินทางรายวันพร้อมที่พัก เที่ยวบิน และการแก้ไขร่วมกัน',
      'ค้นพบแผนการเดินทางที่เผยแพร่ไว้ แล้วเพิ่มลงในทริปของคุณ',
      'รายการสถานที่ที่บันทึกไว้ พร้อมนำเข้าจาก Google Maps',
      'แผนที่เพื่อนและการ์ดแสตมป์ QR',
      'ตรวจหาทริปที่ผ่านมาจากตำแหน่งของรูปภาพ',
      'วิดเจ็ตหน้าจอหลักและสรุปประจำเดือน'
    ],
    videoName: 'ดู Travo ใช้งานจริง — วิดีโอสาธิตแอปบันทึกการเดินทางอัตโนมัติ',
    videoDescription:
      'ดูว่า Travo บันทึกเส้นทางในชีวิตประจำวันและการผจญภัยครั้งใหญ่ของคุณโดยอัตโนมัติอย่างไร และสร้างแผนที่แบบโต้ตอบของทุกที่ที่คุณเคยไป'
  },

  notFound: {
    title: 'ไม่พบหน้านี้ | Travo',
    eyebrow: 'ตีกลับผู้ส่ง',
    heading: joinThaiCompounds('ไม่มีที่อยู่นี้'),
    body: 'หน้าที่คุณกำลังหาอาจถูกย้ายไปแล้ว กลับไปที่หน้าหลักแล้วเริ่มเส้นทางใหม่จากตรงนั้น',
    cta: joinThaiCompounds('กลับหน้าหลัก')
  },

  faqSection: {
    eyebrow: 'คำถาม',
    title: joinThaiCompounds('คำถามที่พบบ่อย'),
    body: 'สรุปสั้น ๆ ว่า Travo ทำอะไร มีค่าใช้จ่ายเท่าไร และใครเห็นการเดินทางของคุณได้บ้าง'
  },

  faq: [
    {
      question: joinThaiCompounds('Travo คืออะไร'),
      answer:
        'Travo คือแอปบันทึกการเดินทางฟรีจาก Travo Labs สำหรับ iOS และ Android ทริปจะเริ่มเองเมื่อคุณออกจากที่อยู่หลัก บันทึกทุกเมืองและทุกประเทศที่ไป ประทับแสตมป์ลงพาสปอร์ตทุกครั้งที่ไปประเทศใหม่ และช่วยวางแผนเที่ยวแบบวันต่อวันร่วมกับเพื่อน'
    },
    {
      question: joinThaiCompounds('Travo มีภาษาไทยไหม'),
      answer:
        'มี แอป Travo และเว็บไซต์นี้รองรับภาษาไทย รวมถึงภาษาอังกฤษ จีนตัวเต็ม ญี่ปุ่น และเกาหลี'
    },
    {
      question: joinThaiCompounds('Travo บันทึกการเดินทางของฉันอย่างไร'),
      answer:
        'Travo ใช้ตำแหน่งเบื้องหลังแบบประหยัดแบตเตอรี่ เมื่อคุณตั้งที่อยู่หลักแล้ว การออกจากที่อยู่หลักจะเริ่มทริปโดยอัตโนมัติ และทุกย่าน ทุกเมือง และทุกประเทศที่คุณผ่านจะถูกบันทึกไว้ — ไม่ต้องพิมพ์อะไรเลย'
    },
    {
      question: joinThaiCompounds('Travo ใช้ฟรีไหม'),
      answer: 'ใช่ ดาวน์โหลดและใช้งาน Travo ได้ฟรีทั้งบน iOS และ Android'
    },
    {
      question: joinThaiCompounds('Travo กินแบตเตอรี่ไหม'),
      answer: 'ไม่ การติดตามเบื้องหลังได้รับการปรับให้ประหยัดแบตเตอรี่ และคุณปิดได้ทุกเมื่อในการตั้งค่า'
    },
    {
      question: joinThaiCompounds('เช็กอินคืออะไร'),
      answer:
        'คุณเช็กอินได้สูงสุดวันละสามครั้งสำหรับช่วงเวลาที่อยากเก็บไว้ เพิ่มรูปภาพแล้วแผนที่จะจำได้ว่าคุณยืนอยู่ตรงไหน เมื่อรวมกันก็กลายเป็นไดอารี่การเดินทางของคุณ'
    },
    {
      question: joinThaiCompounds('พาสปอร์ต Travo คืออะไร'),
      answer:
        'ทุกประเทศที่คุณไปจะเพิ่มแสตมป์ลงในพาสปอร์ต แต่ละปีมีหน้าของตัวเองพร้อมนับจำนวนประเทศและวันที่อยู่นอกบ้าน และเที่ยวบินที่คุณบันทึกจะรวมเป็นระยะทางที่บินเป็นกิโลเมตร'
    },
    {
      question: joinThaiCompounds('วางแผนทริปใน Travo ได้ไหม'),
      answer:
        'ได้ เปลี่ยนสถานที่ที่บันทึกไว้ให้เป็นแผนการเดินทางรายวันพร้อมที่พักและเที่ยวบิน แชร์ให้เพื่อนแก้ไขได้ หรือเพิ่มแผนที่เผยแพร่ในหน้าค้นพบลงในทริปของคุณเอง'
    },
    {
      question: joinThaiCompounds('Travo เพิ่มทริปที่ฉันไปก่อนติดตั้งแอปได้ไหม'),
      answer:
        'ได้ การตรวจหาทริปที่ผ่านมาจะอ่านตำแหน่งที่ถ่ายรูปเพื่อแนะนำทริปก่อนหน้า รูปภาพของคุณจะไม่ถูกอัปโหลดหรือจัดเก็บ'
    },
    {
      question: joinThaiCompounds('ใครเห็นการเดินทางของฉันได้บ้าง'),
      answer:
        'คุณเป็นคนตัดสินใจ ข้อมูลตำแหน่งของคุณเป็นส่วนตัว คุณเลือกได้ว่าเพื่อนคนไหนติดตามคุณได้ และปิดการติดตามได้ทุกเมื่อในการตั้งค่า'
    }
  ]
};
